import type * as zod from 'zod';

import * as log from '@sb/log';
import type { StepFailure } from '@sb/routine-runner/types';
import { EventEmitter } from '@sb/utilities';

import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

const CONFIRM_INTERVAL_CHECK_MS = 10;

const ns = log.namespace('AntigravityStep');
export default class AntigravityStep extends Step<Arguments, Variables> {
  private events: EventEmitter<{
    failure: StepFailure;
  }> = new EventEmitter<{
    failure: StepFailure;
  }>();

  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  protected initializeVariableState(): void {
    this.variables = {
      confirmed: false,
    };
  }

  private checkInterval?: ReturnType<typeof setInterval>;

  private stopConditionalMonitoring?: () => void;

  private resumed = false;

  public confirm(): void {
    this.setVariable('confirmed', true);
  }

  /**
   * Check if the step has been confirmed by its IO condition.
   * Used by RoutineRunner to auto-resume when condition is met.
   */
  public isConditionConfirmed(): boolean {
    return this.variables.confirmed && this.args.condition != null;
  }

  public async _play({ pauseRoutine, fail }: StepPlayArguments): Promise<void> {
    log.info(ns`_play`, 'Antigravity step started');

    const message =
      this.args.message ||
      'Use antigravity to position the arm, then click Resume.';

    this.setVariable('confirmed', false);
    this.resumed = false;

    // Start condition monitoring BEFORE pausing (so it runs while paused)
    // When condition is met, it will set confirmed=true
    this.setStepStopCondition();

    // Set up promise to wait for failure events from conditional evaluation
    const failurePromise = this.events.next('failure').then((failure) => ({
      type: 'failure' as const,
      failure,
    }));

    // Create a promise that resolves when both confirmed AND resumed
    // This ensures resumeRoutine() is called before the step completes,
    // preventing a race condition where the step completes before auto-resume happens
    const confirmationPromise = new Promise<{ type: 'confirmed' }>(
      (resolve) => {
        this.checkInterval = setInterval(async () => {
          if (this.variables.confirmed && this.resumed) {
            if (this.checkInterval) {
              clearInterval(this.checkInterval);
            }

            this.cleanupConditionalMonitoring();

            log.info(ns`_play`, 'Antigravity step confirmed and resumed');

            resolve({ type: 'confirmed' });
          }
        }, CONFIRM_INTERVAL_CHECK_MS);
      },
    );

    // Pause the routine - this sets isPaused=true and returns immediately
    // The user can manually resume, or the condition being met will auto-resume
    // (via confirmStep being called by RoutineRunner's update loop when it detects confirmation)
    await pauseRoutine({
      kind: 'antigravityStep',
      reason: message,
    });

    // Race between confirmation and failure
    // If conditional evaluation fails, fail the step
    // Otherwise, wait for confirmation and resume
    const outcome = await Promise.race([confirmationPromise, failurePromise]);

    if (outcome.type === 'failure') {
      // Conditional evaluation failed - fail the step
      await fail(outcome.failure);
    }

    // Confirmed successfully, step completes
  }

  private setStepStopCondition() {
    if (!this.args.condition) {
      log.info(
        ns`setStepStopCondition`,
        'No condition provided, using confirmation only',
      );

      return;
    }

    const { condition } = this.args;

    // start thread for conditional monitoring every 100ms, exit step if condition is met
    this.stopConditionalMonitoring = this.startConditionalMonitoring(
      condition,
      async ({ isStopped }) => {
        // onConditionMet callback
        if (isStopped()) {
          return;
        }

        log.info(ns`setStepStopCondition`, `Stop condition met, confirming.`, {
          condition: JSON.stringify(condition),
        });

        // Auto-confirm the step when condition is met
        this.confirm();
      },
      (failure, { isStopped }) => {
        // onFailure callback
        if (isStopped()) {
          return;
        }

        log.error(
          ns`setStepStopCondition`,
          'Condition evaluation failed, failing step',
          failure,
        );

        this.events.emit('failure', failure);
      },
      { pollIntervalMs: 100 },
    );
  }

  private cleanupConditionalMonitoring() {
    if (this.stopConditionalMonitoring) {
      this.stopConditionalMonitoring();
      this.stopConditionalMonitoring = undefined;
    }
  }

  public _resume(): void {
    if (!this.variables.confirmed) {
      throw new Error(
        'AntigravityStep needs to be confirmed before it can be resumed.',
      );
    }

    // Mark as resumed so the confirmation promise can complete
    this.resumed = true;
  }

  public _stop() {
    log.info(ns`_stop`, 'Antigravity step stopped');

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = undefined;
    }

    this.cleanupConditionalMonitoring();
    this.resumed = false;
  }

  public hasSelfTimeout(): boolean {
    return false;
  }
}
